import React from 'react'
import styled from 'styled-components'
import { Zap } from 'lucide-react'
import {
  colors,
  spacing,
  mediaQuery,
  borderRadius,
  typographyMixin,
  heading,
  body,
  fontWeight,
} from 'src/constants/marketing-ds'
import XLogo from './XLogo'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { useSelector } from 'react-redux'
import { selectStateForProtobuf } from 'src/modules/reducers'
const VoiceSection = ({ tweetText, onTweetClick }) => {
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  const handleTweetClick = () => {
    onTweetClick()
    fireUIInteractionEvent(
      {
        element: 'Post to X',
        component: 'voice_section_post_to_x_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }
  return (
    <Section>
      <Container>
        <Header>
          <Title>Make Your Voice Heard</Title>
          <Subtitle>
            Don't let NBCU control what you watch. Tell them to stop the unfair practices and come
            back to the negotiating table.
          </Subtitle>
        </Header>
        <Card>
          <CardGrid>
            <CardLeft>
              <Pattern />
              <Gradient />
              <CardContent>
                <IconWrapper>
                  <Zap size={56} />
                </IconWrapper>
                <CardTitle>Stop NBCU's Unfair Practices</CardTitle>
                <CardText>
                  Your voice matters in this fight for fair rates and consumer choice.
                </CardText>
              </CardContent>
            </CardLeft>
            <CardRight>
              <ActionTitle>Share on Social Media</ActionTitle>
              <ActionText>
                Post a message to X (Twitter). Let them know consumers demand fair treatment.
              </ActionText>
              <TweetButton
                href={`https://twitter.com/intent/tweet?text=${tweetText}`}
                target="_blank"
                rel="noreferrer"
                onClick={handleTweetClick}
              >
                <ButtonGradient />
                <ButtonContent>
                  <XLogo size={24} />
                  Post to X
                </ButtonContent>
              </TweetButton>
            </CardRight>
          </CardGrid>
        </Card>
      </Container>
    </Section>
  )
}

export default VoiceSection

const Section = styled.section`
  padding: ${spacing.xxl} ${spacing.sm};
  background-color: ${colors.slate[30]};
  color: ${colors.white};
  ${mediaQuery.tablet} {
    padding: ${spacing.xxxl} ${spacing.md};
  }
`

const Container = styled.div`
  max-width: 1024px;
  margin: 0 auto;
`

const Header = styled.div`
  text-align: left;
  margin-bottom: ${spacing.xl};
  ${mediaQuery.tablet} {
    text-align: center;
    margin-bottom: ${spacing.xxl};
  }
`

const Title = styled.h2`
  ${typographyMixin(heading.lg)}
  margin-bottom: ${spacing.md};
  ${mediaQuery.tablet} {
    ${typographyMixin(heading.xl)}
  }
`

const Subtitle = styled.p`
  color: ${colors.slate[160]};
  ${typographyMixin(body.sm, { fontWeight: fontWeight.normal })}

  max-width: 672px;
  margin: 0 auto;
  line-height: 1.6;
  ${mediaQuery.tablet} {
    ${typographyMixin(body.md, { fontWeight: fontWeight.normal })}
  }

  ${mediaQuery.desktop} {
    ${typographyMixin(body.lg, { fontWeight: fontWeight.normal })}
  }
`

const Card = styled.div`
  background-color: rgb(30, 41, 59);
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.slate[30]};
  overflow: hidden;
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;

  ${mediaQuery.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const CardLeft = styled.div`
  background: linear-gradient(135deg, #ea580c 0%, #dc2626 100%);
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;

  ${mediaQuery.tablet} {
    padding: ${spacing.xl};
  }
  ${mediaQuery.desktop} {
    padding: ${spacing.xxxl};
  }
`

const Pattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.2;
  background-image: url('https://www.transparenttextures.com/patterns/cubes.png');
  mix-blend-mode: overlay;
`

const Gradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.2) 100%);
`

const CardContent = styled.div`
  text-align: center;
  position: relative;
  z-index: 10;
`

const IconWrapper = styled.div`
  display: inline-flex;
  padding: ${spacing.sm};
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border-radius: ${borderRadius.lg};
  margin-bottom: ${spacing.lg};
  box-shadow: 0 0 40px rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);

  svg {
    width: 24px;
    height: 24px;
    ${mediaQuery.tablet} {
      width: 32px;
      height: 32px;
    }
    ${mediaQuery.desktop} {
      width: 56px;
      height: 56px;
    }
  }
`

const CardTitle = styled.h3`
  font-size: 24px;
  font-weight: 800;
  margin-bottom: ${spacing.sm};
  line-height: 1.2;
  color: ${colors.white};
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  ${mediaQuery.tablet} {
    font-size: 36px;
  }

  ${mediaQuery.desktop} {
    font-size: 48px;
  }
`

const CardText = styled.p`
  color: #fed7aa;
  ${typographyMixin(body.md)}
  font-weight: 500;
  max-width: 448px;
  margin: 0 auto;
`

const CardRight = styled.div`
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${mediaQuery.tablet} {
    padding: ${spacing.xl};
    align-items: flex-start;
  }
`

const ActionTitle = styled.h4`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 12px;
  color: ${colors.white};
`

const ActionText = styled.p`
  color: ${colors.slate[160]};
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: ${spacing.lg};
`

const TweetButton = styled.a`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 450px;
  width: 85%;
  gap: 12px;
  background-color: ${colors.white};
  color: ${colors.slate[60]};
  font-weight: 800;
  font-size: 20px;
  padding: ${spacing.sm} ${spacing.lg};
  border-radius: ${borderRadius.lg};
  transition: all 0.2s;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
  text-decoration: none;

  &:hover {
    box-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }
`

const ButtonGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, #fff7ed, ${colors.white});
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: ${borderRadius.lg};

  ${TweetButton}:hover & {
    opacity: 1;
  }
`

const ButtonContent = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
`
